import { z } from 'zod'
import {
  ANNUAL_VOLUMES,
  CONTACT_ROLES,
  REGIONS,
  type AnnualVolume,
  type ContactRole,
  type IntakePrefill,
  type Region,
  type SubmitIntakePayload,
} from '@/lib/api-types'
import { zodFieldErrors, type FieldErrors } from '@/lib/zod-errors'

/**
 * Client-side mirror of the API's public customer-intake zod schema
 * (greenline-hub-api routes/public/intake.ts) — the form validates against
 * it before sending, so what clears here clears the server. The API's
 * roleContacts superRefine (each role exactly once) is guaranteed by
 * construction — the form holds four FIXED role blocks in CONTACT_ROLES
 * order — but the mirror keeps the refinement anyway so the parity test
 * proves it, not just the layout.
 */

export const companySchema = z.object({
  legalName: z.string().trim().min(1).max(255),
  dba: z.string().trim().max(255).optional(),
  preferredDocumentName: z.string().trim().max(255).optional(),
  billingAddress: z.string().trim().min(1).max(1000),
  website: z.string().trim().max(255).optional(),
})

export const roleContactSchema = z.object({
  role: z.enum(CONTACT_ROLES),
  name: z.string().trim().min(1).max(120),
  email: z.email().max(200),
  phone: z.string().trim().max(40).optional(),
})

export const additionalContactSchema = z.object({
  name: z.string().trim().min(1).max(120),
  title: z.string().trim().max(120).optional(),
  email: z.email().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
})

export const shippingSchema = z.object({
  primaryShipTo: z.string().trim().min(1).max(1000),
  additionalShipTos: z.array(z.string().trim().min(1).max(1000)).max(4),
  sampleShipTo: z.string().trim().max(1000).optional(),
  specialDeliveryRequirements: z.string().trim().max(2000).optional(),
})

export const overviewSchema = z.object({
  primaryProductApplications: z.string().trim().min(1).max(2000),
  estimatedAnnualVolume: z.enum(ANNUAL_VOLUMES),
  regions: z.array(z.enum(REGIONS)).min(1),
  regionsOther: z.string().trim().max(255).optional(),
})

export const submissionSchema = z.object({
  company: companySchema,
  roleContacts: z
    .array(roleContactSchema)
    .length(4)
    .superRefine((contacts, ctx) => {
      for (const role of CONTACT_ROLES) {
        const count = contacts.filter((c) => c.role === role).length
        if (count !== 1) {
          ctx.addIssue({
            code: 'custom',
            message: `roleContacts must cover ${role} exactly once (got ${count})`,
          })
        }
      }
    }),
  additionalContacts: z.array(additionalContactSchema).max(3),
  shipping: shippingSchema,
  overview: overviewSchema,
})

// ---------------------------------------------------------------------------
// Form state (string-typed inputs) → API payload
// ---------------------------------------------------------------------------

export const MAX_ADDITIONAL_CONTACTS = 3
export const MAX_ADDITIONAL_SHIP_TOS = 4

export interface RoleContactBlock {
  role: ContactRole
  name: string
  email: string
  phone: string
}

export interface AdditionalContactRow {
  key: string
  name: string
  title: string
  email: string
  phone: string
}

export interface ShipToRow {
  key: string
  address: string
}

export interface IntakeFormState {
  company: {
    legalName: string
    dba: string
    preferredDocumentName: string
    billingAddress: string
    website: string
  }
  /** Always the four fixed blocks, in CONTACT_ROLES order. */
  roleContacts: RoleContactBlock[]
  additionalContacts: AdditionalContactRow[]
  shipping: {
    primaryShipTo: string
    additionalShipTos: ShipToRow[]
    sampleShipTo: string
    specialDeliveryRequirements: string
  }
  overview: {
    primaryProductApplications: string
    estimatedAnnualVolume: string
    regions: Region[]
    regionsOther: string
  }
}

export function emptyAdditionalContact(key: string): AdditionalContactRow {
  return { key, name: '', title: '', email: '', phone: '' }
}

export function emptyShipTo(key: string): ShipToRow {
  return { key, address: '' }
}

/** Prefill what Green Line already knows; the first role block gets the recipient. */
export function initialIntakeForm(prefill: IntakePrefill, contactName: string): IntakeFormState {
  return {
    company: {
      legalName: prefill.legalCompanyName ?? '',
      dba: '',
      preferredDocumentName: '',
      billingAddress: '',
      website: prefill.website ?? '',
    },
    roleContacts: CONTACT_ROLES.map((role, index) => ({
      role,
      name: index === 0 ? contactName : '',
      email: '',
      phone: '',
    })),
    additionalContacts: [],
    shipping: {
      primaryShipTo: '',
      additionalShipTos: [],
      sampleShipTo: '',
      specialDeliveryRequirements: '',
    },
    overview: {
      primaryProductApplications: '',
      estimatedAnnualVolume: '',
      regions: [],
      regionsOther: '',
    },
  }
}

/** '' → undefined so optional fields are OMITTED from the payload, not sent blank. */
function opt(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** The exact POST body — undefined optionals drop out of the JSON entirely. */
export function buildIntakePayload(form: IntakeFormState): SubmitIntakePayload {
  return {
    company: {
      legalName: form.company.legalName.trim(),
      dba: opt(form.company.dba),
      preferredDocumentName: opt(form.company.preferredDocumentName),
      billingAddress: form.company.billingAddress.trim(),
      website: opt(form.company.website),
    },
    roleContacts: form.roleContacts.map((block) => ({
      role: block.role,
      name: block.name.trim(),
      email: block.email.trim(),
      phone: opt(block.phone),
    })),
    additionalContacts: form.additionalContacts.map((row) => ({
      name: row.name.trim(),
      title: opt(row.title),
      email: opt(row.email),
      phone: opt(row.phone),
    })),
    shipping: {
      primaryShipTo: form.shipping.primaryShipTo.trim(),
      additionalShipTos: form.shipping.additionalShipTos.map((row) => row.address.trim()),
      sampleShipTo: opt(form.shipping.sampleShipTo),
      specialDeliveryRequirements: opt(form.shipping.specialDeliveryRequirements),
    },
    overview: {
      primaryProductApplications: form.overview.primaryProductApplications.trim(),
      estimatedAnnualVolume: form.overview.estimatedAnnualVolume as AnnualVolume,
      regions: form.overview.regions,
      // regionsOther only travels when Other is actually picked (the API
      // keeps it optional either way — the form just doesn't send strays).
      regionsOther: form.overview.regions.includes('Other')
        ? opt(form.overview.regionsOther)
        : undefined,
    },
  }
}

/** Keys match the form's field paths (`company.legalName`, `roleContacts.0.email`, …). */
export function validateIntake(form: IntakeFormState): FieldErrors {
  return zodFieldErrors(submissionSchema.safeParse(buildIntakePayload(form)))
}
